import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { reviewService } from '../../../services/reviewService';
import { useAuth } from '../../../contexts/AuthContext';
import { validateTurkishContent } from '../../../utils/turkishValidation';

const ReviewFormModal = ({ isOpen, onClose, spotId, spotName, onReviewSubmitted }) => {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  if (!isOpen) return null;

  const handleImageChange = (e) => {
    const files = Array.from(e?.target?.files);
    if (files?.length + imageFiles?.length > 5) {
      setError('Maksimum 5 fotoğraf yükleyebilirsiniz');
      return;
    }

    setImageFiles([...imageFiles, ...files]);
    
    // Create preview URLs
    const newPreviewUrls = files?.map(file => URL.createObjectURL(file));
    setImagePreviewUrls([...imagePreviewUrls, ...newPreviewUrls]);
    setError('');
  };

  const handleRemoveImage = (index) => {
    const newFiles = imageFiles?.filter((_, i) => i !== index);
    const newPreviews = imagePreviewUrls?.filter((_, i) => i !== index);
    
    // Revoke the URL to free memory
    URL.revokeObjectURL(imagePreviewUrls?.[index]);
    
    setImageFiles(newFiles);
    setImagePreviewUrls(newPreviews);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!user) {
      setError('Yorum yapmak için giriş yapmalısınız');
      return;
    }

    if (rating === 0) {
      setError('Lütfen bir puan seçin');
      return;
    }

    if (!comment?.trim()) {
      setError('Lütfen yorum yazın');
      return;
    }

    const commentError = validateTurkishContent(comment, 'Yorum', 5);
    if (commentError) {
      setError(commentError);
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      // For now, we'll pass empty array for image URLs
      // In a real implementation, you'd upload images to Supabase Storage first
      const imageUrls = [];

      await reviewService?.createReview(spotId, rating, comment?.trim(), imageUrls);

      setSuccessMessage('Yorumunuz gönderildi, onaylandıktan sonra yayınlanacaktır.');
      
      // Reset form
      setRating(0);
      setComment('');
      setImageFiles([]);
      setImagePreviewUrls([]);

      // Call callback to refresh reviews list
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }

      // Close modal after 2 seconds
      setTimeout(() => {
        setSuccessMessage('');
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error submitting review:', err);
      setError(err?.message || 'Yorum gönderilirken bir hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Clean up preview URLs
    imagePreviewUrls?.forEach(url => URL.revokeObjectURL(url));
    setImagePreviewUrls([]);
    setImageFiles([]);
    setRating(0);
    setComment('');
    setError('');
    setSuccessMessage('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <h2 className="text-xl md:text-2xl font-bold text-foreground">
            {spotName} için Yorum Yaz
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            disabled={isSubmitting}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-6">
          {/* Rating Section */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Puanınız *
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5]?.map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="transition-transform hover:scale-110"
                >
                  <Icon
                    name="Star"
                    size={32}
                    className={`${
                      star <= (hoveredRating || rating)
                        ? 'text-yellow-500 fill-yellow-500' :'text-muted-foreground'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Comment Section */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Yorumunuz *
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e?.target?.value)}
              placeholder="Bu spot hakkındaki deneyimlerinizi paylaşın..."
              className="w-full p-3 border border-border rounded-lg bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              rows={5}
              maxLength={500}
              disabled={isSubmitting}
            />
            <p className="text-xs text-muted-foreground mt-1 text-right">
              {comment?.length}/500 karakter
            </p>
          </div>

          {/* Image Upload Section */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Fotoğraflar (İsteğe bağlı)
            </label>
            <div className="space-y-3">
              {imagePreviewUrls?.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {imagePreviewUrls?.map((url, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="absolute top-1 right-1 p-1 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {imageFiles?.length < 5 && (
                <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                  <Icon name="Upload" size={32} className="text-muted-foreground mb-2" />
                  <span className="text-sm text-muted-foreground">
                    Fotoğraf yüklemek için tıklayın
                  </span>
                  <span className="text-xs text-muted-foreground mt-1">
                    Maksimum 5 fotoğraf (JPG, PNG)
                  </span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    multiple
                    onChange={handleImageChange}
                    className="hidden"
                    disabled={isSubmitting}
                  />
                </label>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <Icon name="AlertCircle" size={18} className="text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="flex items-start gap-2 p-3 bg-success/10 border border-success/20 rounded-lg">
              <Icon name="CheckCircle" size={18} className="text-success flex-shrink-0 mt-0.5" />
              <p className="text-sm text-success">{successMessage}</p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={handleClose}
              disabled={isSubmitting}
            >
              İptal
            </Button>
            <Button
              type="submit"
              fullWidth
              disabled={isSubmitting || rating === 0 || !comment?.trim()}
              iconName={isSubmitting ? undefined : "Send"}
            >
              {isSubmitting ? 'Gönderiliyor...' : 'Yorumu Gönder'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewFormModal;